import type { LucideIcon } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { logger } from '@/lib/logger';

// Import commonly used icons directly to ensure they're available during static generation
import { Shield, Zap, FolderTree, XCircle, Filter, ShieldCheck, Circle } from 'lucide-react';

/**
 * Static map of commonly used icons for reliable access during static generation.
 * This ensures icons are available even if dynamic access fails.
 */
const iconMap: Record<string, LucideIcon> = {
  Shield,
  Zap,
  FolderTree,
  XCircle,
  Filter,
  ShieldCheck,
  Circle,
};

/**
 * Map icon name string to Lucide React icon component
 *
 * This utility allows storing icon names as strings in data files
 * and mapping them to actual icon components at runtime.
 *
 * @param iconName - Name of the icon (must match lucide-react export name)
 * @returns The icon component, or a default icon if not found
 *
 * @example
 * ```typescript
 * const ShieldIcon = getIcon('Shield');
 * <ShieldIcon className="h-5 w-5" />
 * ```
 *
 * @throws Never throws - returns a default icon if name not found
 */
export function getIcon(iconName: string): LucideIcon {
  // First check the static map (most reliable during static generation)
  if (iconName in iconMap) {
    return iconMap[iconName];
  }

  // Fallback to dynamic access for other icons
  const iconsRecord = LucideIcons as unknown as Record<string, LucideIcon | undefined>;

  // Use 'in' operator to check if icon exists, then verify it's a function
  if (iconName in iconsRecord) {
    const Icon = iconsRecord[iconName];
    if (Icon && typeof Icon === 'function') {
      return Icon;
    }
  }

  // Fallback to a default icon if not found
  logger.warn('Icon not found in lucide-react, using fallback', {
    iconName,
    fallback: 'Circle',
  });
  return Circle;
}

/**
 * Type guard to check if an icon name exists in lucide-react
 *
 * @param iconName - Name of the icon to check
 * @returns True if the icon exists, false otherwise
 */
export function iconExists(iconName: string): boolean {
  // First check the static map
  if (iconName in iconMap) {
    return true;
  }

  // Fallback to dynamic check
  return (
    iconName in LucideIcons &&
    typeof (LucideIcons as unknown as Record<string, unknown>)[iconName] === 'function'
  );
}
